import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import indexedDBService from '../services/indexedDBService';
import { useDateRange } from '../context/DateContext';

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF7C43', '#665191', '#FF6B6B'
];

// Active account managers - only show these in reporting
const ACTIVE_ACCOUNT_MANAGERS = [
  'Rochelle Williams',
  'Kelly Eastcott',
  'Jane Pryce',
  'Lisa Culshaw',
  'Annie Crawford',
  'Tina Whitbread',
  'Alex Hampton'
];

// KPI threshold for emails sent per account manager per location
const EMAILS_SENT_KPI = 5; // Minimum number of emails to meet KPI

const Marketing = () => {
  const navigate = useNavigate();
  const { startDate, endDate } = useDateRange();
  
  // All hooks must be called before any conditional returns
  const [loading, setLoading] = useState(true);
  const [industries, setIndustries] = useState([]);
  const [typeOfHires, setTypeOfHires] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedTypeOfHires, setSelectedTypeOfHires] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [locations, setLocations] = useState([]);
  // New state variables for Pure Promote data
  const [purePromoteData, setPurePromoteData] = useState(null);
  const [purePromoteLoading, setPurePromoteLoading] = useState(false);
  const [purePromoteError, setPurePromoteError] = useState(null);

  // Update dashboard when filters change
  const updateDashboard = async (industries, types, start, end) => {
    try {
      console.log('Updating dashboard with:', { 
        industries, 
        types, 
        startDate: start?.format('YYYY-MM-DD'), 
        endDate: end?.format('YYYY-MM-DD') 
      });

      // Ensure we have valid dayjs objects
      const startDate = dayjs(start).startOf('day');
      const endDate = dayjs(end).endOf('day');

      const [filteredMetrics, filteredOpportunities, allLocations] = await Promise.all([
        indexedDBService.getFilteredMetrics({
          industries,
          typeOfHires: types,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }),
        indexedDBService.filterOpportunities({
          industries,
          typeOfHires: types,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }),
        indexedDBService.getAllLocations()
      ]);

      // console.log('Raw filtered opportunities:', filteredOpportunities);

      if (!Array.isArray(filteredOpportunities)) {
        console.error('Filtered opportunities is not an array:', filteredOpportunities);
        return;
      }

      // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      // !!! CRITICAL WARNING: DO NOT MODIFY OPPORTUNITY STRUCTURE          !!!
      // !!! The PurePromote contact list depends on the complete original   !!!
      // !!! opportunity structure, especially Location_Name__r and          !!!
      // !!! Primary_Contact__r objects. Always preserve these using spread. !!!
      // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      
      // Map the opportunities to ensure all required fields are present
      const mappedOpportunities = filteredOpportunities
        .map((opp, index) => {
          if (!opp) {
            console.error(`Null opportunity at index ${index}`);
            return null;
          }

          // CRITICAL: Always preserve entire original structure for PurePromote compatibility
          // Any changes here could break the PurePromote contact list functionality!
          const mapped = {
            ...opp, // Keep ALL original fields - DO NOT REMOVE THIS SPREAD
            Id: opp.Id || `missing-id-${index}`,
            clientName: opp.clientName || 'N/A',
            Name: opp.Name || 'N/A',
            typeOfHire: opp.Type_of_H__c || opp.typeOfHire || 'N/A',
            industry: opp.industry || 'N/A',
            Total_Hire_Fees__c: Number(opp.Total_Hire_Fees__c) || 0,
            Gross_Profit__c: Number(opp.Gross_Profit__c) || 0,
            CloseDate: opp.CloseDate || null,
            locationName: opp.Location_Name__r?.Name || 'N/A',
            locationAddress: opp.Location_Name__r?.Address__c || 'N/A'
          };

          return mapped;
        })
        .filter(opp => {
          // Remove opportunities with no revenue and no location
          const hasRevenue = opp?.Total_Hire_Fees__c > 0;
          const hasLocation = opp?.locationName !== 'N/A';
          return hasRevenue && hasLocation;
        });

      // Get the set of location IDs from the filtered opportunities
      const chosenLocationIds = new Set(
        mappedOpportunities
          .map(opp => opp.Location_Name__r?.Id)
          .filter(id => id != null)
      );

   

      // Filter locations to only include those that are chosen in the filtered opportunities
      const filteredLocations = allLocations.filter(location => {
        const isChosen = chosenLocationIds.has(location.accountId);
     
        return isChosen;
      });

     

      setMetrics(filteredMetrics);
      setOpportunities(mappedOpportunities);
      setLocations(filteredLocations);

      console.log('Filtered locations:', {
        totalLocations: allLocations.length,
        filteredCount: filteredLocations.length,
        chosenLocationIds: Array.from(chosenLocationIds)
      });
    } catch (error) {
      console.error('Error updating dashboard:', error);
    }
  };

  // Function to load data based on current filters and date range
  const loadData = async (start, end) => {
    try {
      setLoading(true);
      // console.log('Loading data with date range:', { start, end });

      const [uniqueIndustries, uniqueTypeOfHires, locations] = await Promise.all([
        indexedDBService.getUniqueIndustries(),
        indexedDBService.getUniqueTypeOfHires(),
        indexedDBService.getAllLocations()
      ]);

      console.log('Loaded initial data:', {
        industries: uniqueIndustries,
        typeOfHires: uniqueTypeOfHires,
        locationCount: locations?.length
      });

      setIndustries(uniqueIndustries);
      setTypeOfHires(uniqueTypeOfHires);
      setLocations(locations);
      await updateDashboard(selectedIndustries, selectedTypeOfHires, start, end);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (startDate && endDate) {
      loadData(startDate, endDate);
    }
  }, [startDate, endDate]);

  // Listen for global date changes
  useEffect(() => {
    const handleGlobalDateChange = (event) => {
      const { startDate: newStartDate, endDate: newEndDate } = event.detail;
      loadData(dayjs(newStartDate), dayjs(newEndDate));
    };

    window.addEventListener('globalDateChange', handleGlobalDateChange);

    return () => {
      window.removeEventListener('globalDateChange', handleGlobalDateChange);
    };
  }, [selectedIndustries, selectedTypeOfHires]);

  // Handle filter changes
  const handleIndustryChange = (event) => {
    const value = event.target.value;
    setSelectedIndustries(value);
    updateDashboard(value, selectedTypeOfHires, startDate, endDate);
  };

  const handleTypeOfHireChange = (event) => {
    const value = event.target.value;
    setSelectedTypeOfHires(value);
    updateDashboard(selectedIndustries, value, startDate, endDate);
  };

  // DataGrid columns for opportunities
  const opportunityColumns = [
    { 
      field: 'clientName', 
      headerName: 'Client', 
      flex: 1
    },
    { 
      field: 'Name', 
      headerName: 'Opportunity', 
      flex: 1.5
    },
    { 
      field: 'typeOfHire', 
      headerName: 'Type of Hire', 
      flex: 1
    },
    { 
      field: 'industry', 
      headerName: 'Industry', 
      flex: 1
    },
    {
      field: 'Total_Hire_Fees__c',
      headerName: 'Revenue',
      flex: 1,
      type: 'number',
      valueFormatter: (params) => {
        if (params == null) return '$0.00';
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params);
      }
    },
    {
      field: 'Gross_Profit__c',
      headerName: 'Profit',
      flex: 1,
      type: 'number',
      valueFormatter: (params) => {
        if (params == null) return '$0.00';
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params);
      }
    },
    {
      field: 'locationName',
      headerName: 'Location',
      flex: 1.5
    },
    {
      field: 'locationAddress',
      headerName: 'Address',
      flex: 2
    },
    {
      field: 'CloseDate',
      headerName: 'Close Date',
      flex: 1,
      type: 'date',
      valueFormatter: (params) => {
        if (!params) return '';
        return dayjs(params).format('DD/MM/YYYY');
      }
    }
  ];

  // DataGrid columns for locations
  const locationColumns = [
    { 
      field: 'name', 
      headerName: 'Location Name', 
      flex: 1.5
    },
    { 
      field: 'address', 
      headerName: 'Address', 
      flex: 2
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1
    },
    { 
      field: 'design', 
      headerName: 'Design', 
      flex: 1
    },
    { 
      field: 'locationTier', 
      headerName: 'Tier', 
      flex: 0.5
    },
    {
      field: 'lastSyncedAt',
      headerName: 'Last Updated',
      flex: 1,
      valueFormatter: (params) => {
        if (!params.value) return 'N/A';
        return dayjs(params.value).format('DD/MM/YYYY HH:mm');
      }
    }
  ];

  // Prepare chart data
  const prepareIndustryChartData = () => {
    if (!metrics?.byIndustry) return [];
    return Object.entries(metrics.byIndustry).map(([industry, data]) => ({
      name: industry,
      revenue: data.revenue,
      profit: data.profit,
      count: data.count
    }));
  };

  const prepareTypeOfHireChartData = () => {
    if (!metrics?.byTypeOfHire) return [];
    return Object.entries(metrics.byTypeOfHire).map(([type, data]) => ({
      name: type,
      value: data.revenue
    }));
  };

  const prepareSuburbChartData = () => {
    if (!opportunities.length) return [];
    
    // Extract suburbs from location names
    const suburbCounts = opportunities.reduce((acc, opp) => {
      const locationParts = opp.locationName.split(',');
      if (locationParts.length > 1) {
        const suburb = locationParts[1].trim();
        acc[suburb] = (acc[suburb] || 0) + 1;
      }
      return acc;
    }, {});

    // Convert to array and sort by count
    return Object.entries(suburbCounts)
      .map(([suburb, count]) => ({
        name: suburb,
        count
      }))
      .sort((a, b) => b.count - a.count);
  };

  // DataGrid columns for Pure Promote emails
  const purePromoteColumns = [
    { 
      field: 'Name', 
      headerName: 'Email ID', 
      flex: 1
    },
    { 
      field: 'contactName', 
      headerName: 'Contact', 
      flex: 1.5
    },
    { 
      field: 'contactEmail', 
      headerName: 'Email', 
      flex: 1.5
    },
    { 
      field: 'locationName', 
      headerName: 'Location Promoted', 
      flex: 1.5
    },
    {
      field: 'accountManager',
      headerName: 'Account Manager',
      flex: 1.2
    },
    {
      field: 'Send_Datetime__c',
      headerName: 'Send Date',
      flex: 1,
      valueFormatter: (params) => {
        if (!params) return '';
        try {
          // Use dayjs for more reliable formatting
          return dayjs(params).format('D/MM h:mm a');
        } catch (error) {
          console.error('Error formatting date:', error, params);
          return String(params);
        }
      }
    }
  ];

  // Data preparation functions for Pure Promote emails
  const prepareEmailTimeSeriesData = () => {
    if (!purePromoteData?.emails) return [];
    
    // Group emails by date
    const emailsByDate = purePromoteData.emails.reduce((acc, email) => {
      const date = dayjs(email.Send_Datetime__c).format('YYYY-MM-DD');
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});
    
    // Convert to array format for chart
    return Object.entries(emailsByDate)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());
  };

  const prepareEmailsByLocationData = () => {
    if (!purePromoteData?.emails) return [];
    
    // Group emails by location
    const emailsByLocation = purePromoteData.emails.reduce((acc, email) => {
      // Use both approaches to get location name to handle different data structures
      const locationName = email.Location_Account_Promoted__r?.Name || 
                         (email.attributes?.type === 'Pure_Promote__c' ? 'Unknown' : email.locationName) || 
                         'Unknown';
      
      acc[locationName] = (acc[locationName] || 0) + 1;
      return acc;
    }, {});
    
    // Convert to array format for chart and take top 10
    return Object.entries(emailsByLocation)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  };

  // Prepare data for location breakdown by account manager
  const prepareLocationByAccountManagerData = () => {
    if (!purePromoteData?.emails) return [];
    
    // Use only active account managers
    const allAccountManagers = new Set(ACTIVE_ACCOUNT_MANAGERS);
    
    // Group by location, then by account manager
    const locationMap = new Map();
    
    purePromoteData.emails.forEach(email => {
      const locationName = email.locationName || 'Unknown';
      const accountManager = email.accountManager || 'Unassigned';
      
      // Only include active account managers
      if (!ACTIVE_ACCOUNT_MANAGERS.includes(accountManager)) {
        return;
      }
      
      if (!locationMap.has(locationName)) {
        locationMap.set(locationName, {
          locationName,
          accountManagers: new Map()
        });
      }
      
      const locationData = locationMap.get(locationName);
      const currentCount = locationData.accountManagers.get(accountManager) || 0;
      locationData.accountManagers.set(accountManager, currentCount + 1);
    });
    
    // Convert to array format - include ALL active account managers for each location (even with 0 sends)
    return Array.from(locationMap.values()).map(location => {
      // Create a map with all active account managers, defaulting to 0 for those who didn't send
      const allManagersForLocation = new Map();
      allAccountManagers.forEach(managerName => {
        const count = location.accountManagers.get(managerName) || 0;
        allManagersForLocation.set(managerName, count);
      });
      
      return {
        locationName: location.locationName,
        accountManagers: Array.from(allManagersForLocation.entries())
          .map(([name, count]) => ({ 
            name, 
            count,
            meetsKPI: count >= EMAILS_SENT_KPI
          }))
          .sort((a, b) => b.count - a.count), // Sort by count descending
        totalEmails: Array.from(allManagersForLocation.values())
          .reduce((sum, count) => sum + count, 0)
      };
    }).sort((a, b) => b.totalEmails - a.totalEmails);
  };

  // Calculate account manager KPI performance
  const prepareAccountManagerKPIData = () => {
    if (!purePromoteData?.emails) return [];
    
    const locationData = prepareLocationByAccountManagerData();
    
    // Initialize stats for all active account managers
    const managerStats = new Map();
    ACTIVE_ACCOUNT_MANAGERS.forEach(managerName => {
      managerStats.set(managerName, {
        name: managerName,
        totalLocations: 0,
        locationsMetKPI: 0,
        totalEmailsSent: 0
      });
    });
    
    // Calculate for each account manager: how many locations they met KPI for
    locationData.forEach(location => {
      location.accountManagers.forEach(manager => {
        if (!managerStats.has(manager.name)) {
          return; // Skip if not in active list
        }
        
        const stats = managerStats.get(manager.name);
        stats.totalLocations++;
        stats.totalEmailsSent += manager.count;
        
        if (manager.meetsKPI) {
          stats.locationsMetKPI++;
        }
      });
    });
    
    // Calculate average KPI percentage
    return Array.from(managerStats.values())
      .map(stat => ({
        ...stat,
        averageKPI: stat.totalLocations > 0 
          ? (stat.locationsMetKPI / stat.totalLocations * 100).toFixed(1)
          : '0.0',
        averageKPINumber: stat.totalLocations > 0 
          ? stat.locationsMetKPI / stat.totalLocations * 100
          : 0
      }))
      .sort((a, b) => b.averageKPINumber - a.averageKPINumber); // Sort by average KPI descending
  };

  // Function to fetch Pure Promote data - wrapped in useCallback
  const fetchPurePromoteData = useCallback(async () => {
    try {
      setPurePromoteLoading(true);
      setPurePromoteError(null);
      
      // console.log('Current opportunities data available for lookup:', opportunities.length);
      
      // Format dates for query
      const formattedStartDate = startDate.format('YYYY-MM-DD');
      const formattedEndDate = endDate.format('YYYY-MM-DD');
      
      const requestBody = {
        startDate: `${formattedStartDate}T00:00:00Z`,
        endDate: `${formattedEndDate}T23:59:59Z`,
        industries: selectedIndustries,
        typeOfHires: selectedTypeOfHires,
        cacheBuster: new Date().getTime() // Add cache buster to prevent cached responses
      };
      
      // console.log('Fetching Pure Promote data with params:', requestBody);
      
      // Always use production server URL
      const apiUrl = 'https://bdm.purelocations.com.au/api/sf/marketingPurePromote';
      // console.log('Using API URL:', apiUrl);
      
      try {
        // Call the server endpoint
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          credentials: 'include', // Include cookies for authentication if needed
        });
        
        // console.log('Response status:', response.status, response.statusText);
        
        // Check content type to detect HTML response
        const contentType = response.headers.get('content-type');
        // console.log('Response content type:', contentType);
        
        if (contentType && contentType.includes('text/html')) {
          const htmlText = await response.text();
          console.error('Received HTML instead of JSON:', htmlText.substring(0, 200) + '...');
          throw new Error('Server returned HTML instead of JSON. The API endpoint might not exist or is misconfigured.');
        }
        
        if (!response.ok) {
          // Try to parse as JSON, but handle case where it's not JSON
          let errorData;
          const responseText = await response.text();
          
          try {
            errorData = JSON.parse(responseText);
          } catch (e) {
            console.error('Error parsing response as JSON:', responseText.substring(0, 200) + '...');
            throw new Error(`Server returned non-JSON response with status ${response.status}`);
          }
          
          console.error('API error response:', errorData);
          throw new Error(errorData.details || errorData.error || 'Failed to fetch Pure Promote data');
        }
        
        // Safely parse JSON
        let data;
        try {
          const responseText = await response.text();
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Error parsing successful response as JSON:', e);
          throw new Error('Invalid JSON response from server');
        }
        
        if (!data || !data.records) {
          console.error('Invalid response format:', data);
          throw new Error('No data returned from API');
        }
        
        // console.log('Pure Promote data received:', data.records.length, 'records');
        
        // Log detailed information about the first record to help with debugging
        if (data.records && data.records.length > 0) {
          // console.log('First record structure:', JSON.stringify(data.records[0], null, 2));
          // console.log('Contact relationship available:', !!data.records[0]?.Contact_Sent_To__r);
          // console.log('Location relationship available:', !!data.records[0]?.Location_Account_Promoted__r);
          // console.log('Related opportunity available:', !!data.records[0]?.RelatedOpportunity);
        }
        
        // Process the data directly - no need for additional client-side lookups
        const processedEmails = data.records
          .map(record => {
            // Determine account manager - prefer Contact Owner, fallback to Pure_Promote__c Owner
            const accountManager = record.Contact_Sent_To__r?.Owner?.Name || 
                                  record.Owner?.Name || 
                                  'Unassigned';
            const accountManagerEmail = record.Contact_Sent_To__r?.Owner?.Email || 
                                       record.Owner?.Email || 
                                       '';
            
            // Only include active account managers
            if (!ACTIVE_ACCOUNT_MANAGERS.includes(accountManager)) {
              return null;
            }
            
            // Create a processed record with flattened structure for the DataGrid
            return {
              id: record.Id || record.Name,
              Name: record.Name,
              Send_Datetime__c: record.Send_Datetime__c,
              // Extract relationship data into direct properties
              contactName: record.Contact_Sent_To__r?.Name || 'N/A',
              contactEmail: record.Contact_Sent_To__r?.Email || 'N/A',
              locationName: record.Location_Account_Promoted__r?.Name || 'N/A',
              locationId: record.Location_Account_Promoted__c,
              // Account manager information
              accountManager: accountManager,
              accountManagerEmail: accountManagerEmail,
              // Keep the RelatedOpportunity structure from the server
              RelatedOpportunity: record.RelatedOpportunity,
              AllRelatedOpportunities: record.AllRelatedOpportunities,
              // Store the full original record for reference
              originalRecord: record
            };
          })
          .filter(email => email !== null); // Remove null entries (excluded account managers)
        
        
        // Process data
        const processed = {
          // Use the pre-processed emails with flattened structure
          emails: processedEmails,
          totalEmails: data.records.length,
          uniqueLocations: new Set(data.records
            .filter(r => r && r.Location_Account_Promoted__c)
            .map(r => r.Location_Account_Promoted__c)).size,
          uniqueContacts: new Set(data.records
            .filter(r => r && r.Contact_Sent_To__c)
            .map(r => r.Contact_Sent_To__c)).size
        };
        
        // console.log('Processed emails structure:', processedEmails[0]);
        
        setPurePromoteData(processed);
      } catch (fetchError) {
        // Handle specific fetch errors like CORS or network issues
        if (fetchError.message.includes('Failed to fetch') || 
            fetchError.message.includes('NetworkError') ||
            fetchError.message.includes('CORS')) {
          console.error('Network or CORS error:', fetchError);
          throw new Error('Unable to connect to the server. This might be due to network issues or CORS restrictions.');
        }
        
        // Re-throw other errors
        throw fetchError;
      }
    } catch (error) {
      console.error('Error fetching Pure Promote data:', error);
      setPurePromoteError(error.message);
    } finally {
      setPurePromoteLoading(false);
    }
  }, [startDate, endDate, selectedIndustries, selectedTypeOfHires]);

  // Update to fetch Pure Promote data when dashboard filters change
  useEffect(() => {
    if (startDate && endDate) {
      fetchPurePromoteData();
    }
  }, [startDate, endDate, fetchPurePromoteData]);

  // Check if user is admin - redirect if not (after all hooks)
  useEffect(() => {
    const userRole = localStorage.getItem('purelytics_role');
    if (userRole !== 'admin') {
      // Redirect to clients page if not admin
      navigate('/clients', { replace: true });
    }
  }, [navigate]);
  
  // Get userRole to check access
  const userRole = localStorage.getItem('purelytics_role');
  
  // Don't render content if not admin (after all hooks are called)
  if (userRole !== 'admin') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          This page is only accessible to administrators.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Marketing Dashboard
      </Typography>

      {/* Pure Promote Email Reporting Section */}
      <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
        Pure Promote Email Reporting
      </Typography>

      {/* Email Metrics Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Emails Sent</Typography>
              <Typography variant="h4">
                {purePromoteData?.totalEmails || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Unique Locations Promoted</Typography>
              <Typography variant="h4">
                {purePromoteData?.uniqueLocations || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Unique Contacts</Typography>
              <Typography variant="h4">
                {purePromoteData?.uniqueContacts || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Email Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Emails Sent Over Time</Typography>
              {purePromoteLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={prepareEmailTimeSeriesData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#8884d8" name="Emails Sent" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Emails by Location</Typography>
              {purePromoteLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={prepareEmailsByLocationData()}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(entry) => entry.name}
                    >
                      {prepareEmailsByLocationData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pure Promote Data Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Pure Promote Emails</Typography>
          {purePromoteLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : purePromoteError ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography color="error" gutterBottom>Error loading Pure Promote data:</Typography>
              <Typography variant="body2" color="error" align="center" sx={{ maxWidth: '80%', whiteSpace: 'pre-wrap' }}>
                {purePromoteError}
              </Typography>
              <Box mt={2}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => fetchPurePromoteData()}
                  size="small"
                >
                  Retry
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={purePromoteData?.emails || []}
                  columns={purePromoteColumns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 50, page: 0 },
                    },
                  }}
                  rowCount={purePromoteData?.totalEmails || 0}
                  getRowId={(row) => row.id || row.Name || `email-${Math.random()}`}
                  disableSelectionOnClick
                  density="compact"
                  sx={{
                    '& .MuiDataGrid-cell': {
                      fontSize: '0.875rem',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                    }
                  }}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Account Manager KPI Performance Cards */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Account Manager KPI Performance
            </Typography>
          </Box>
          {purePromoteLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : purePromoteError ? (
            <Typography color="error">
              Unable to load account manager KPI data
            </Typography>
          ) : (
            <Box>
              {prepareAccountManagerKPIData().length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                  No account manager data available for the selected date range
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {prepareAccountManagerKPIData().map((manager, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          border: `2px solid ${manager.averageKPINumber >= 50 ? '#4caf50' : '#f44336'}`,
                          backgroundColor: manager.averageKPINumber >= 50 ? '#e8f5e9' : '#ffebee'
                        }}
                      >
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {manager.name}
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: manager.averageKPINumber >= 50 ? '#2e7d32' : '#c62828' }}>
                              {manager.averageKPI}%
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                              Average KPI
                            </Typography>
                          </Box>
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                            <Typography variant="body2" color="textSecondary">
                              Met KPI at {manager.locationsMetKPI} of {manager.totalLocations} location{manager.totalLocations !== 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                              Total emails sent: {manager.totalEmailsSent}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Location Breakdown by Account Manager */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Location Breakdown by Account Manager
            </Typography>
          </Box>
          {purePromoteLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : purePromoteError ? (
            <Typography color="error">
              Unable to load account manager breakdown
            </Typography>
          ) : (
            <Box>
              {prepareLocationByAccountManagerData().length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                  No location data available for the selected date range
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ 
                    '& .MuiTableCell-root': { 
                      borderBottom: '1px solid #e0e0e0',
                      borderRight: '1px solid #e0e0e0',
                      '&:last-child': { borderRight: 'none' }
                    },
                    '& .MuiTableHead .MuiTableCell-root': {
                      borderBottom: '2px solid #bdbdbd'
                    }
                  }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                            Location Promoted
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                            Account Manager
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, width: '15%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <EmailIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                            Emails Sent
                          </Box>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontWeight: 'normal' }}>
                            KPI: {EMAILS_SENT_KPI}+
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, width: '15%' }}>
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {prepareLocationByAccountManagerData().map((location, locationIndex) => (
                        <React.Fragment key={locationIndex}>
                          {location.accountManagers.map((manager, managerIndex) => (
                            <TableRow 
                              key={`${locationIndex}-${managerIndex}`}
                              sx={{ 
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                '&:last-child td': { borderBottom: locationIndex < prepareLocationByAccountManagerData().length - 1 ? '2px solid #bdbdbd' : '1px solid #e0e0e0' }
                              }}
                            >
                              {managerIndex === 0 && (
                                <>
                                  <TableCell 
                                    rowSpan={location.accountManagers.length}
                                    sx={{ 
                                      fontWeight: 600,
                                      verticalAlign: 'top',
                                      pt: 2
                                    }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {location.locationName}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {location.totalEmails} total email{location.totalEmails !== 1 ? 's' : ''}
                                    </Typography>
                                  </TableCell>
                                </>
                              )}
                              <TableCell 
                                sx={{ 
                                  backgroundColor: manager.meetsKPI ? '#e8f5e9' : '#ffebee',
                                  '&:hover': { backgroundColor: manager.meetsKPI ? '#c8e6c9' : '#ffcdd2' }
                                }}
                              >
                                <Typography variant="body2">
                                  {manager.name}
                                </Typography>
                              </TableCell>
                              <TableCell 
                                align="right"
                                sx={{ 
                                  backgroundColor: manager.meetsKPI ? '#e8f5e9' : '#ffebee',
                                  '&:hover': { backgroundColor: manager.meetsKPI ? '#c8e6c9' : '#ffcdd2' }
                                }}
                              >
                                <Chip 
                                  label={manager.count} 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: manager.meetsKPI ? '#4caf50' : '#f44336',
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                              {managerIndex === 0 && (
                                <TableCell 
                                  rowSpan={location.accountManagers.length}
                                  align="right"
                                  sx={{ 
                                    fontWeight: 600,
                                    verticalAlign: 'top',
                                    pt: 2,
                                    color: 'primary.main'
                                  }}
                                >
                                  {location.totalEmails}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Industries</InputLabel>
            <Select
              multiple
              value={selectedIndustries}
              onChange={handleIndustryChange}
              input={<OutlinedInput label="Industries" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {industries.map((industry) => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Type of Hire</InputLabel>
            <Select
              multiple
              value={selectedTypeOfHires}
              onChange={handleTypeOfHireChange}
              input={<OutlinedInput label="Type of Hire" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {typeOfHires.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Metrics Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Opportunities</Typography>
              <Typography variant="h4">
                {metrics?.totalOpportunities.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Revenue</Typography>
              <Typography variant="h4">
                {new Intl.NumberFormat('en-AU', { 
                  style: 'currency', 
                  currency: 'AUD',
                  maximumFractionDigits: 0 
                }).format(metrics?.totalRevenue || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Revenue by Industry</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={prepareIndustryChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => 
                      new Intl.NumberFormat('en-AU', { 
                        style: 'currency', 
                        currency: 'AUD',
                        maximumFractionDigits: 0 
                      }).format(value)
                    }
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#0088FE" name="Revenue" />
                  <Bar dataKey="profit" fill="#00C49F" name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Revenue by Type of Hire</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={prepareTypeOfHireChartData()}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => entry.name}
                  >
                    {prepareTypeOfHireChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => 
                      new Intl.NumberFormat('en-AU', { 
                        style: 'currency', 
                        currency: 'AUD',
                        maximumFractionDigits: 0 
                      }).format(value)
                    }
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Opportunities by Suburb</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={prepareSuburbChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" name="Number of Opportunities" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Opportunities Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Opportunities</Typography>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={opportunities}
              columns={opportunityColumns}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 50, page: 0 },
                },
              }}
              rowCount={opportunities.length}
              getRowId={(row) => row.Id || `opp-${Math.random()}`}
              disableSelectionOnClick
              density="compact"
              sx={{
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      
    </Box>
  );
};

export default Marketing; 